import classNames from 'classnames';
import { type FC, type ReactNode, useId } from 'react';
import { FormFieldError } from '../FormFieldError/FormFieldError';
import { FormFieldHelperText } from '../FormFieldHelperText/FormFieldHelperText';
import { Label } from '../Label/Label';
import type { InteractiveAtomSize } from '../utils/InteractiveAtomProps';
import type {
  FormFieldChildrenModeProps,
  FormFieldProps,
  FormFieldRenderControlModeProps,
  FormFieldSize,
} from './FormField.types';

const descriptionSizeClassName: { [Key in InteractiveAtomSize]: string } = {
  small: 'text-xs',
  default: 'text-sm',
  large: 'text-base',
};

/*
 *  Wrapper component providing form fields (Radio, Checkbox, TextInput, etc.) with labels and supporting texts.
 *
 *  Two render modes:
 *  - Default `children` mode renders the supplied child between chrome elements,
 *    matching the original behaviour. No internal ids are emitted on chrome.
 *  - `renderControl` mode emits stable ids on label / description / helper /
 *    error elements and hands them to the child via `FormFieldIds`. Use this
 *    for controls that need to wire `aria-labelledby` / `aria-describedby`
 *    themselves (e.g. CodeEditor's contenteditable). Existing callers passing
 *    `children` are unaffected.
 */
export const FormField: FC<FormFieldProps> = (props) =>
  props.renderControl ? (
    <FormFieldRenderControl {...props} />
  ) : (
    <FormFieldChildren {...props} />
  );

const FormFieldRenderControl: FC<FormFieldRenderControlModeProps> = ({
  className,
  description,
  disabled,
  error,
  primaryHelperText,
  id,
  label,
  labelTooltip,
  link,
  onLabelClick,
  renderControl,
  secondaryHelperText,
  size = 'default',
}) => {
  const reactId = useId();
  const baseId = id ?? reactId;
  const showError = error !== undefined && error !== '';
  // Mirrors the truthiness check inside FormFieldHelperTexts so we never emit
  // a `helperId` that points at a node that isn't rendered (e.g. when the
  // caller clears helper text reactively with `''` / `null` / `false`).
  const showHelpers =
    !showError && Boolean(primaryHelperText || secondaryHelperText);

  const labelId = label ? `${baseId}-label` : undefined;
  const descriptionId = description ? `${baseId}-description` : undefined;
  const helperId = showHelpers ? `${baseId}-helper` : undefined;
  const errorId = showError ? `${baseId}-error` : undefined;

  return (
    <div className={classNames('flex flex-col gap-3xs', className)}>
      {label && (
        <div className='flex flex-row justify-between align-center'>
          <Label
            id={labelId}
            tooltipContent={labelTooltip}
            size={size}
            htmlFor={baseId}
            onClick={onLabelClick}
          >
            {label}
          </Label>
          {link}
        </div>
      )}
      {description && (
        <span
          id={descriptionId}
          className={classNames('text-plain', descriptionSizeClassName[size])}
        >
          {description}
        </span>
      )}
      {renderControl({ baseId, labelId, descriptionId, helperId, errorId })}
      {showError ? (
        <span id={errorId}>
          <FormFieldError error={error} size={size} disabled={disabled} />
        </span>
      ) : (
        <FormFieldHelperTexts
          id={helperId}
          size={size}
          disabled={disabled}
          primaryText={primaryHelperText}
          secondaryText={secondaryHelperText}
        />
      )}
    </div>
  );
};

const FormFieldChildren: FC<FormFieldChildrenModeProps> = ({
  children,
  className,
  description,
  disabled,
  error,
  primaryHelperText,
  htmlFor,
  label,
  labelTooltip,
  link,
  secondaryHelperText,
  size = 'default',
}) => (
  <div className={classNames('flex flex-col gap-3xs', className)}>
    {label && (
      <div className='flex flex-row justify-between align-center'>
        <Label tooltipContent={labelTooltip} size={size} htmlFor={htmlFor}>
          {label}
        </Label>
        {link}
      </div>
    )}
    {description && (
      <span
        className={classNames('text-plain', descriptionSizeClassName[size])}
      >
        {description}
      </span>
    )}
    {children}
    {error ? (
      <FormFieldError error={error} size={size} disabled={disabled} />
    ) : (
      <FormFieldHelperTexts
        size={size}
        disabled={disabled}
        primaryText={primaryHelperText}
        secondaryText={secondaryHelperText}
      />
    )}
  </div>
);

const FormFieldHelperTexts = ({
  disabled,
  id,
  primaryText,
  secondaryText,
  size = 'default',
}: {
  disabled?: boolean;
  id?: string;
  primaryText?: ReactNode;
  secondaryText?: ReactNode;
  size?: FormFieldSize;
}) => {
  if (!primaryText && !secondaryText) {
    return null;
  }

  return (
    <div
      id={id}
      className={`flex px-xs ${!primaryText ? 'justify-end' : 'justify-between'}`}
    >
      {primaryText && (
        <FormFieldHelperText size={size} disabled={disabled}>
          {primaryText}
        </FormFieldHelperText>
      )}
      {secondaryText && (
        <FormFieldHelperText size={size} disabled={disabled}>
          {secondaryText}
        </FormFieldHelperText>
      )}
    </div>
  );
};
